import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { opportunitiesService } from '../services/opportunities.service';
import type {
  OpportunityListFilters,
  Opportunity,
} from '../types/internal-opportunity.types';
import type { PaginationParams } from '../types/opportunity.types';

const OPPORTUNITIES_KEY = 'opportunities';
const OPPORTUNITY_KEY = 'opportunity';
const OPPORTUNITY_STATS_KEY = 'opportunity-stats';
const OPPORTUNITY_PIPELINE_KEY = 'opportunity-pipeline';

/**
 * Hook pour récupérer la liste paginée des opportunités
 */
export function useOpportunities(
  filters: OpportunityListFilters = {},
  pagination: Partial<PaginationParams> = {}
) {
  return useQuery({
    queryKey: [OPPORTUNITIES_KEY, filters, pagination],
    queryFn: () => opportunitiesService.getAll(filters, pagination),
  });
}

/**
 * Hook pour récupérer une opportunité par son ID
 */
export function useOpportunity(id: number | undefined) {
  return useQuery({
    queryKey: [OPPORTUNITY_KEY, id],
    queryFn: () => opportunitiesService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook pour récupérer les statistiques des opportunités
 */
export function useOpportunityStats() {
  return useQuery({
    queryKey: [OPPORTUNITY_STATS_KEY],
    queryFn: () => opportunitiesService.getStats(),
  });
}

/**
 * Hook pour mettre à jour une opportunité
 */
export function useUpdateOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Opportunity> }) =>
      opportunitiesService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_KEY] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_KEY, id] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_STATS_KEY] });
    },
  });
}

/**
 * Hook pour mettre à jour le statut d'une opportunité
 */
export function useUpdateOpportunityStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      opportunitiesService.updateStatus(id, status),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_KEY] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_KEY, id] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_STATS_KEY] });
    },
  });
}

/**
 * Hook pour enregistrer une décision GO/NO-GO
 */
export function useMakeDecision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      decision,
      reason,
    }: {
      id: number;
      decision: 'go' | 'no_go';
      reason?: string;
    }) => opportunitiesService.makeDecision(id, decision, reason),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_KEY] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_KEY, id] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_STATS_KEY] });
    },
  });
}

/**
 * Hook pour supprimer une opportunité
 */
export function useDeleteOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => opportunitiesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_KEY] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_STATS_KEY] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_PIPELINE_KEY] });
    },
  });
}

/**
 * Hook pour récupérer le pipeline Kanban des opportunités
 */
export function useOpportunityPipeline(filters: Partial<OpportunityListFilters> = {}) {
  return useQuery({
    queryKey: [OPPORTUNITY_PIPELINE_KEY, filters],
    queryFn: () => opportunitiesService.getPipeline(filters),
  });
}

/**
 * Hook pour mettre à jour le statut avec rafraîchissement du pipeline
 */
export function useUpdateOpportunityStatusKanban() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      opportunitiesService.updateStatus(id, status),
    onSuccess: async (_, { id }) => {
      // Refetch immédiatement le pipeline pour avoir les données à jour
      await queryClient.refetchQueries({ queryKey: [OPPORTUNITY_PIPELINE_KEY] });
      // Invalider les autres queries
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_KEY] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_KEY, id] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_STATS_KEY] });
    },
  });
}

/**
 * Hook pour rafraîchir les données d'une opportunité depuis sa source externe (BOAMP)
 */
export function useRefreshOpportunityFromSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => opportunitiesService.refreshFromSource(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_KEY, id] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_KEY] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_PIPELINE_KEY] });
    },
  });
}
